import { ButtonProps, Box, Button } from '@cad-arc';
import styles from './unread-messages-button.module.css';
import classnames from 'classnames';
import { Virtualizer } from '@tanstack/react-virtual';
import { useEffect, useMemo, useState } from 'react';
import { AssignedParticipantUnreadInfo, ConversationMessageView } from '@mark43/cad-api';
import throttle from 'lodash/debounce';
import { CHECK_FOR_READ_CONVERSATION_THROTTLE_MILLIS } from '../constants';
import { ConversationKey } from '../models';

const ArrowButton = ({
    position,
    isUrgent = false,
    ...rest
}: ButtonProps & {
    onClick?: (e?: unknown) => void;
    isUrgent?: boolean;
    position: 'top' | 'bottom';
}): React.JSX.Element => {
    return (
        <Button
            variant="solid"
            leadingVisual={position === 'top' ? 'ArrowUp' : 'ArrowDown'}
            borderRadius="var(--arc-radii-full)"
            className={classnames(
                position === 'top' ? styles['top-button'] : styles['bottom-button'],
                isUrgent ? styles['urgent-button'] : styles['not-urgent-button']
            )}
            {...rest}
        />
    );
};

export const UnreadMessagesButton: React.FC<{
    messageRowVirtualizer: Virtualizer<HTMLDivElement, Element>;
    conversationMessages: ConversationMessageView[];
    unreadInfo?: AssignedParticipantUnreadInfo;
    getMessageIndexById?: (id: number) => number | void;
    conversationKey?: ConversationKey;
}> = ({ messageRowVirtualizer, conversationMessages, unreadInfo, getMessageIndexById }) => {
    const virtualMessageRows = messageRowVirtualizer.getVirtualItems();

    const [buttonState, setButtonState] = useState<{
        position: 'top' | 'bottom';
        isUrgent?: boolean;
    } | null>(null);

    const scrollToMessageId = (messageId: number): void => {
        const scrollToMessageIndex = getMessageIndexById?.(messageId);

        if (scrollToMessageIndex !== undefined) {
            const lastMessageIndex = conversationMessages.length - 1;

            if (lastMessageIndex < 0 || scrollToMessageIndex > lastMessageIndex) {
                messageRowVirtualizer.scrollToIndex(lastMessageIndex, {
                    align: 'start',
                });
            } else {
                messageRowVirtualizer.scrollToIndex(scrollToMessageIndex, {
                    align: 'start',
                });
            }
        }
    };

    const throttledUpdateButtonState = useMemo(() => {
        return throttle(
            () => {
                if (
                    unreadInfo === undefined ||
                    unreadInfo?.oldestUnreadMessageId === undefined ||
                    virtualMessageRows.length === 0 ||
                    conversationMessages === undefined ||
                    conversationMessages.length === 0
                ) {
                    setButtonState(null);
                    return;
                }

                const { hasUrgentUnread, oldestUnreadMessageId } = unreadInfo;

                const topVisibleMessage = conversationMessages[virtualMessageRows[0].index];
                const bottomVisibleMessage =
                    conversationMessages[virtualMessageRows[virtualMessageRows.length - 1].index];

                if (topVisibleMessage === undefined || bottomVisibleMessage === undefined) {
                    return;
                }

                const bottomMessage = conversationMessages[conversationMessages.length - 1];

                if (
                    oldestUnreadMessageId < topVisibleMessage.id ||
                    (oldestUnreadMessageId === topVisibleMessage.id &&
                        messageRowVirtualizer.scrollOffset > virtualMessageRows[0].start)
                ) {
                    setButtonState({ position: 'top', isUrgent: hasUrgentUnread });
                } else if (
                    oldestUnreadMessageId > bottomVisibleMessage.id &&
                    oldestUnreadMessageId <= bottomMessage.id
                ) {
                    setButtonState({ position: 'bottom', isUrgent: hasUrgentUnread });
                } else {
                    setButtonState(null);
                }
            },
            CHECK_FOR_READ_CONVERSATION_THROTTLE_MILLIS,
            // leading set to true so that a little time passes before the initial execution. this avoid the button
            // pointing down before the conversation messages scrollTo has completed.
            { leading: true }
        );
    }, [conversationMessages, messageRowVirtualizer.scrollOffset, unreadInfo, virtualMessageRows]);

    useEffect(() => {
        throttledUpdateButtonState();
    }, [
        virtualMessageRows,
        conversationMessages,
        unreadInfo,
        unreadInfo?.oldestUnreadMessageId,
        messageRowVirtualizer.scrollOffset,
        throttledUpdateButtonState,
    ]);

    return (
        <Box>
            {buttonState !== null && (
                <ArrowButton
                    data-testid="jump-to-unread-message-button"
                    position={buttonState.position}
                    isUrgent={buttonState.isUrgent}
                    onClick={() => {
                        if (unreadInfo?.oldestUnreadMessageId !== undefined) {
                            scrollToMessageId(unreadInfo?.oldestUnreadMessageId);
                        }
                    }}
                >
                    {buttonState.isUrgent ? 'Urgent update' : 'New messages'}
                </ArrowButton>
            )}
        </Box>
    );
};
